import {
  AlgorithmTargetFormDataType,
  AlgorithmTargetRuleValidFormDataType,
} from './schema/types';

export function getMaxRuleCode(rules?: AlgorithmTargetRuleValidFormDataType[]) {
  return (rules || []).reduce(
    (acc, rule) => (acc > Number(rule.ruleCode) ? acc : Number(rule.ruleCode)),
    0,
  );
}

export function getMaxTargetSeqNr(targets?: AlgorithmTargetFormDataType[]) {
  return (targets || []).reduce(
    (acc, target) =>
      acc > Number(target.sequenceNumber) ? acc : Number(target.sequenceNumber),
    0,
  );
}

export const encodeRuleSourceItemValue = (
  data:
    | {
        algorithmSourceId: number;
        sourceItemCode: string;
      }
    | {
        algorithmMappingId: number;
      },
) =>
  'algorithmSourceId' in data
    ? encodeRuleAlgorithmSourceValue(
        data.algorithmSourceId,
        data.sourceItemCode,
      )
    : encodeRuleAlgorithmMappingValue(data.algorithmMappingId);

// algorithm source ID and dimension code
export type AlgorithmTargetRuleAlgorithmSourceValue = `s-${number}-${string}`;

export const encodeRuleAlgorithmSourceValue = (
  algorithmSourceId: number,
  sourceItemCode: string,
): AlgorithmTargetRuleAlgorithmSourceValue =>
  `s-${algorithmSourceId}-${sourceItemCode}` as const;

// algorithm mapping ID
export type AlgorithmTargetRuleAlgorithmMappingValue = `m-${number}`;

export const encodeRuleAlgorithmMappingValue = (
  algorithmMappingId: number,
): AlgorithmTargetRuleAlgorithmMappingValue =>
  `m-${algorithmMappingId}` as const;

export type DecodedRuleSourceItemValue =
  | {}
  | {
      algorithmSourceId: number;
      sourceItemCode: string;
    }
  | {
      algorithmMappingId: number;
    };

export const decodeRuleSourceItemValue = (
  value?: string | null,
): DecodedRuleSourceItemValue => {
  if (!value) {
    return {};
  }

  if (value.startsWith('s-')) {
    // Source ref: `s-<algorithmSourceId>-<sourceItemCode>`
    const [, algorithmSourceIdString, ...sourceItemCodeParts] =
      value.split('-');
    const algorithmSourceId = Number(algorithmSourceIdString);
    const sourceItemCode = sourceItemCodeParts.join('-');

    return {
      algorithmSourceId,
      sourceItemCode,
    };
  } else {
    // Mapping ref: `m-<algorithmMappingId>`
    const [, algorithmMappingIdString] = value.split('-');
    const algorithmMappingId = Number(algorithmMappingIdString);

    return {
      algorithmMappingId,
    };
  }
};

export const encodeRuleTargetItemValue = (
  data: { code: string; dimensionType: any } | { code: string },
  confidentialityStatus?: boolean,
): `dim-${string}` | `ind-${string}` | `ind-${string}-conf` =>
  'dimensionType' in data
    ? (`dim-${data.code}` as const)
    : confidentialityStatus
      ? (`ind-${data.code}-conf` as const)
      : (`ind-${data.code}` as const);

export type DecodedRuleTargetItemValue =
  | {
      dimensionCode: string;
    }
  | {
      indicatorCode: string;
      confidentialityStatus: boolean;
    };

export const decodeRuleTargetItemValue = (
  values?: string[] | null,
): DecodedRuleTargetItemValue[] => {
  if (!values || values.length === 0) {
    return [];
  }

  return values.reduce((decodedValues, value) => {
    const [type, code, conf] = value.split('-');
    return decodedValues.concat(
      type === 'dim'
        ? {
            dimensionCode: code,
          }
        : {
            indicatorCode: code,
            confidentialityStatus: Boolean(conf),
          },
    );
  }, [] as DecodedRuleTargetItemValue[]);
};
